<template>
  <div class="clue-select-component">
    <div v-if="disabled" class="disabled-box"></div>
    <el-dropdown
      ref="dropdownRef"
      placement="bottom"
      popper-class="clue-select-component__popper"
      trigger="click"
      @visible-change="handleVisibleChange"
    >
      <div class="clue-select-component__dropdown" style="width: 248px">
        <div v-if="modelValue?.id" class="clue-select-component__name text-overflow">
          {{ modelValue.name }}
        </div>
        <div v-else class="clue-select-component__placeholder">请选择线索</div>
        <i class="icon-arrow-down-s-line t-iconfont" />
      </div>
      <template #dropdown>
        <el-dropdown-menu style="width: 248px; padding: 0">
          <div class="clue-select-component__search">
            <el-input
              ref="inputRef"
              v-model="searchValue"
              clearable
              placeholder="请输入线索名称"
              :prefix-icon="Search"
              @input="handleSearch"
            />
          </div>
          <div class="clue-select-component__desc">按匹配度从高到低排序</div>
          <div class="clue-select-component__list" @scroll="handleScroll">
            <div v-if="loading" class="clue-select-component__status">加载中...</div>
            <div
              v-else-if="loadError"
              class="clue-select-component__status clue-select-component__status--action"
              role="button"
              @click="resetAndLoad"
            >
              加载失败，点击重试
            </div>
            <div v-else-if="clueList.length === 0" class="clue-select-component__status">
              暂无匹配结果
            </div>
            <template v-else>
              <el-dropdown-item
                v-for="item in clueList"
                :key="item.id"
                @click="handleSelectClue(item)"
              >
                <span class="text-overflow">{{ item.name }}</span>
              </el-dropdown-item>
              <div v-if="loadingMore" class="clue-select-component__status">加载中...</div>
              <div
                v-else-if="loadMoreError"
                class="clue-select-component__status clue-select-component__status--action"
                role="button"
                @click="loadMore"
              >
                加载失败，点击重试
              </div>
            </template>
          </div>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import { Search } from '@element-plus/icons-vue'
import { getClueList } from '@/api'

const PAGE_SIZE = 20
const SEARCH_DELAY = 300

const modelValue = defineModel('modelValue', { type: Object, required: true })
defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['change'])

const inputRef = useTemplateRef('inputRef')
const dropdownRef = ref(null)
const searchValue = ref('')
const clueList = ref([])
const page = ref(1)
const rowsCount = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const loadError = ref(false)
const loadMoreError = ref(false)

let searchTimer = null
let queryVersion = 0

const hasMore = computed(() => clueList.value.length < rowsCount.value)

const fetchPage = async (pageNumber, version) => {
  try {
    const { result } = await getClueList({
      page: pageNumber,
      pageSize: PAGE_SIZE,
      nameLike: searchValue.value.trim()
    })
    if (version !== queryVersion) return

    const nextList = result?.clueList || []
    clueList.value = pageNumber === 1 ? nextList : [...clueList.value, ...nextList]
    page.value = pageNumber
    rowsCount.value = result?.pageHelper?.rowsCount || 0
  } catch (error) {
    if (version === queryVersion) {
      console.error('获取线索列表失败:', error)
      if (pageNumber === 1) loadError.value = true
      else loadMoreError.value = true
    }
  } finally {
    if (version === queryVersion) {
      loading.value = false
      loadingMore.value = false
    }
  }
}

const resetAndLoad = () => {
  const version = ++queryVersion
  page.value = 1
  rowsCount.value = 0
  clueList.value = []
  loadError.value = false
  loadMoreError.value = false
  loading.value = true
  loadingMore.value = false
  fetchPage(1, version)
}

const handleSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(resetAndLoad, SEARCH_DELAY)
}

const loadMore = () => {
  if (loading.value || loadingMore.value || !hasMore.value) return
  loadMoreError.value = false
  loadingMore.value = true
  fetchPage(page.value + 1, queryVersion)
}

const handleScroll = (event) => {
  const target = event.currentTarget
  if (target.scrollHeight - target.scrollTop - target.clientHeight <= 20) {
    loadMore()
  }
}

const handleVisibleChange = (visible) => {
  if (!visible) return
  resetAndLoad()
  setTimeout(() => inputRef.value?.focus?.({ preventScroll: true }), 300)
}

const handleSelectClue = (item) => {
  modelValue.value = { id: item.id, name: item.name }
  dropdownRef.value?.handleClose?.()
  emit('change', modelValue.value)
}

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
  queryVersion++
})
</script>

<style lang="less">
.clue-select-component__popper {
  .el-dropdown-menu {
    overflow: hidden;
  }
}
</style>

<style scoped lang="less">
.clue-select-component {
  flex: 1;
  position: relative;
  width: 100%;

  .disabled-box {
    position: absolute;
    inset: 0;
    background: rgba(243, 246, 249, 0.6);
    z-index: 10;
    cursor: not-allowed;
  }

  .el-dropdown {
    width: 100%;
  }
}

.clue-select-component__dropdown {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  box-sizing: border-box;
}

.clue-select-component__name {
  flex: 1;
  width: 100%;
  margin-right: 8px;
}

.clue-select-component__placeholder {
  color: #c8c9cc;
}

.clue-select-component__search {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 5px 16px;
  background: #fff;
}

.clue-select-component__desc {
  margin: 5px 16px;
  color: #969799;
  font-size: 12px;
  line-height: 16px;
}

.clue-select-component__list {
  height: min(260px, calc(100vh - 100px));
  overflow-y: auto;
}

.clue-select-component__status {
  padding: 24px 16px;
  color: #969799;
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  &--action {
    cursor: pointer;
  }
}

.text-overflow {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
